// Layout do RETORNO (prefeitura -> Atlas) — fonte unica de verdade.
//
// ZIP com DOIS arquivos CSV `;`, sem cabecalho, UM layout plano cada. Nenhum
// tipo de registro: cada arquivo e' uma tabela.
//
//   carga.csv        1 linha por servidor   cadastro, situacao, salario, banco
//   consignados.csv  1 linha por divida     resultado do desconto e cronograma
//
// A forma vem de um pacote REAL — ver specs/domain/referencia-consigfacil.md. O
// arquivo de consignados espelha as 9 colunas do `retorno-consignados.csv` do
// ConsigFacil na MESMA ordem, entao um municipio que ja gera aquele arquivo so
// precisa passar a preencher a coluna 7 (numero do contrato) com a ADF.
//
// Por que nao um arquivo unico: as cardinalidades sao diferentes — cadastro e' 1
// por servidor, consignado e' N por divida. Fundir exigiria tipo de registro (o
// que o ERP nao quer) ou repetir o cadastro inteiro em cada linha de desconto.
//
// DESLIGAMENTO VEM COMO VALOR, nao como ausencia. O campo `situacao` da carga e'
// a declaracao; matricula que simplesmente nao aparece NAO desliga ninguem, e e'
// isso que separa "a prefeitura decidiu" de "o export veio truncado" (ADR-0001).
import { CODIGO_RESULTADO, } from "@atlas/types";
import { lerLinhasDelimitadas, parsearLinhaColunas, validarLayoutColunas } from "./csv-delimitado.js";
import { normalizarAlfa } from "./fixed-width.js";
export const RETORNO_LAYOUT_VERSAO = "3.0";
export const RETORNO_SEPARADOR = ";";
/** Nomes reconhecidos dentro do zip. A deteccao e' por SUBSTRING no nome, sem
 *  distinguir maiuscula: o ERP costuma anexar competencia ao nome do arquivo
 *  (`rubrica_funcionarios20267.csv`), e exigir nome exato faria o pacote real
 *  ser recusado por um detalhe de nomenclatura. */
export const ARQUIVO_CARGA = "carga";
export const ARQUIVO_CONSIGNADOS = "consignados";
/** Situacao funcional declarada pela prefeitura. `DE` e `AP` sao DESLIGAMENTO:
 *  tiram o contrato da folha e notificam o banco. `LI` e `CE` mantem o servidor
 *  na base sem folha no mes — e, desde 16/09/2026, ficam gravados na pessoa;
 *  `CE` fecha a averbacao, `LI` nao (ver situacao-funcional.ts). */
export const SITUACAO_CARGA = ["AT", "DE", "AP", "LI", "CE"];
/** As duas que disparam o cascade irreversivel (cobranca direta + e-mail). */
export const SITUACOES_DESLIGAMENTO = ["DE", "AP"];
/** Situacao em que o servidor nao teve folha no mes, mas continua na base.
 *  Salario NAO e' comparado nestes casos — nao houve holerite. */
export const SITUACOES_SEM_FOLHA = ["LI", "CE"];
export const COLUNAS_CARGA = [
    { nome: "competencia", coluna: 1, kind: "competencia", obrigatorio: true, descricao: "Competência, AAAAMM. Precisa bater com a folha para a qual o pacote foi enviado." },
    { nome: "matricula", coluna: 2, kind: "alfa", obrigatorio: true, descricao: "Matrícula do servidor. Apenas dígitos; preserve os zeros à esquerda." },
    // ===== IDENTIDADE E MARGEM: obrigatorios em TODA linha, nao so' na admissao
    // (decisao do cliente, 28/08/2026).
    //
    // Antes, `cpf`, `nome` e `salarioLiquido` eram opcionais e a linha de um
    // servidor novo sem eles era descartada em SILENCIO pelo aplicador: nao
    // virava admissao, nao gerava erro e nao entrava em contador nenhum. O
    // servidor simplesmente nao existia — ficava sem margem, e os consignados
    // dele caiam em quarentena todo mes sem causa apontavel.
    //
    // Medido antes de exigir: o arquivo real do I.M.A.M.N. preenche os tres em
    // todas as 55 linhas. A exigencia nao pede nada que o ERP ja nao exporte.
    { nome: "cpf", coluna: 3, kind: "cpf", obrigatorio: true, descricao: "CPF do servidor. É ele, e não a matrícula, que identifica a pessoa — matrícula é numerada por folha." },
    { nome: "nome", coluna: 4, kind: "alfa", obrigatorio: true, descricao: "Nome do servidor." },
    { nome: "situacao", coluna: 5, kind: "codigo", obrigatorio: true, codigos: SITUACAO_CARGA, descricao: "`AT` ativo · `DE` desligado · `AP` aposentado · `LI` licença · `CE` cedido. **`DE` e `AP` são declaração de desligamento** e tiram o contrato da folha. `LI` e `CE` ficam registrados na pessoa; `CE` **fecha a averbação** até a folha declará-la `AT` de novo, `LI` não — a margem sai do salário informado e o retorno decide mês a mês." },
    { nome: "vinculo", coluna: 6, kind: "alfa", descricao: "Vínculo (EFETIVO, CLT, COMISSIONADO…)." },
    { nome: "cargo", coluna: 7, kind: "alfa", descricao: "Cargo. Comparado com o cadastro; diferença de acento ou caixa não conta como mudança." },
    { nome: "dataAdmissao", coluna: 8, kind: "dataBr", descricao: "Data de admissão, **DDMMAAAA**." },
    // Obrigatorio = a coluna precisa vir PREENCHIDA, e nao que o valor seja > 0:
    // `000000000000,00` e' resposta valida e significa "nao houve folha para ele
    // neste mes". Vazio nao e', porque vazio e zero sao indistinguiveis depois de
    // parseados (`dinheiro` vazio vira 0) — e a diferenca entre "ganhou nada" e
    // "o ERP nao mandou" e' a diferenca entre margem zero e margem desconhecida.
    { nome: "salarioLiquido", coluna: 9, kind: "dinheiro", obrigatorio: true, descricao: "Salário líquido — base do cálculo de margem. Preencha sempre; `000000000000,00` é válido e significa que não houve folha no mês. Não entra no diff quando a situação é `LI` ou `CE`." },
    { nome: "bancoCodigo", coluna: 10, kind: "alfa", descricao: "Código do banco de crédito do salário. Vazio não apaga o que a Atlas já tem." },
    { nome: "agencia", coluna: 11, kind: "alfa", descricao: "Agência. Vazio não apaga." },
    { nome: "conta", coluna: 12, kind: "alfa", descricao: "Conta. Vazio não apaga." },
    // NAO existe coluna de convenio aqui. O servidor pertence a PREFEITURA, nao a
    // um convenio dela (regra do cliente 25/08/2026) — a prefeitura pode ter
    // varios, e o banco que tem convenio com ela ve a base toda. Quem pertence a
    // um convenio e' o CONTRATO. Pedir convenio na carga obrigaria a prefeitura a
    // classificar servidor por banco, o que nao tem significado no dominio.
];
/** Espelha `retorno-consignados.csv` do ConsigFacil, coluna a coluna. */
export const COLUNAS_CONSIGNADOS = [
    { nome: "matricula", coluna: 1, kind: "alfa", obrigatorio: true, descricao: "Matrícula do servidor." },
    { nome: "codigoVerba", coluna: 2, kind: "alfa", descricao: "Código da verba/rubrica de desconto." },
    { nome: "descricao", coluna: 3, kind: "alfa", descricao: "Descrição da verba, como aparece no holerite. Usada para identificar a consignatária quando a dívida é desconhecida." },
    { nome: "tipo", coluna: 4, kind: "codigo", codigos: ["D", "P"], descricao: "`D` = desconto. Linhas `P` (provento) são ignoradas." },
    { nome: "valor", coluna: 5, kind: "dinheiro", obrigatorio: true, descricao: "Valor efetivamente descontado. Aceita `000000000229,76` e `229.76`." },
    { nome: "parcelasRestantes", coluna: 6, kind: "num", descricao: "Parcelas que ainda faltam, incluindo a deste mês." },
    // A coluna que resolve a ambiguidade. No pacote real ela vem VAZIA, e e' por
    // isso que existe o fallback de casamento: ver `casarConsignado`.
    { nome: "referencia", coluna: 7, kind: "alfa", descricao: "**Número do contrato.** Devolva aqui a **ADF** que veio na remessa — é o que permite casar sem ambiguidade quando o servidor tem duas dívidas na mesma verba." },
    { nome: "parcelaAtual", coluna: 8, kind: "num", descricao: "Número da parcela descontada nesta competência." },
    { nome: "parcelasTotal", coluna: 9, kind: "num", descricao: "Total de parcelas do contrato." },
];
/** Quantas colunas o layout declara. Tudo alem disso e' campo da prefeitura. */
const COLUNAS_DECLARADAS_CARGA = COLUNAS_CARGA.length;
function vazio() {
    return {
        rejeitado: true,
        motivoRejeicao: null,
        competencia: null,
        carga: [],
        consignados: [],
        linhasRejeitadas: [],
        proventosIgnorados: 0,
    };
}
/** Confere que os dois layouts estao coerentes. Chamado na entrada do parser
 *  porque coluna duplicada faz um campo sobrescrever o outro em silencio. */
export function validarLayoutsRetorno() {
    return [
        ...validarLayoutColunas(COLUNAS_CARGA).map((p) => `carga: ${p}`),
        ...validarLayoutColunas(COLUNAS_CONSIGNADOS).map((p) => `consignados: ${p}`),
    ];
}
export function parsearRetorno(bruto) {
    const out = vazio();
    const problemas = validarLayoutsRetorno();
    if (problemas.length > 0)
        throw new Error(`layout do retorno inconsistente: ${problemas.join("; ")}`);
    const linhasCarga = lerLinhasDelimitadas(bruto.carga, RETORNO_SEPARADOR);
    const linhasCons = lerLinhasDelimitadas(bruto.consignados, RETORNO_SEPARADOR);
    if (linhasCarga.length === 0) {
        out.motivoRejeicao = "arquivo de carga vazio — sem ele nao ha base pra conferir nem competencia declarada";
        return out;
    }
    // ===== Carga. Erro em DECLARACAO de desligamento derruba o pacote; erro em
    // linha comum rejeita so a linha. E' o mesmo racional do tudo-ou-nada
    // anterior, agora por CAMPO em vez de por tipo de registro: meia declaracao
    // aplicada deixaria parte dos contratos fora da folha sem ninguem decidir.
    const errosDeclaracao = [];
    const errosSalario = [];
    const matriculasVistas = new Set();
    const competencias = new Set();
    for (let i = 0; i < linhasCarga.length; i++) {
        const numero = i + 1;
        const { valores, erros } = parsearLinhaColunas(COLUNAS_CARGA, linhasCarga[i]);
        const situacao = String(valores.situacao ?? "");
        const matricula = String(valores.matricula ?? "");
        if (matricula !== "" && matriculasVistas.has(matricula)) {
            erros.push({ campo: "matricula", coluna: 2, mensagem: "matricula repetida na carga" });
        }
        const declaraDesligamento = SITUACOES_DESLIGAMENTO.includes(situacao);
        if (erros.length > 0) {
            // Salario liquido ausente/invalido derruba o PACOTE, nao so' a linha: sem
            // ele a margem do servidor fica DESCONHECIDA (nao zero), e uma base com
            // margem furada e' pior que reenviar. Decisao do cliente (Q1=A, 2026-08-29).
            if (erros.some((e) => e.campo === "salarioLiquido")) {
                errosSalario.push({ arquivo: ARQUIVO_CARGA, linha: numero, erros });
            }
            else {
                (declaraDesligamento ? errosDeclaracao : out.linhasRejeitadas).push({ arquivo: ARQUIVO_CARGA, linha: numero, erros });
            }
            continue;
        }
        matriculasVistas.add(matricula);
        competencias.add(String(valores.competencia ?? ""));
        out.carga.push({
            linha: numero,
            competencia: String(valores.competencia ?? ""),
            matricula,
            cpf: String(valores.cpf ?? ""),
            nome: String(valores.nome ?? ""),
            situacao,
            vinculo: String(valores.vinculo ?? ""),
            cargo: String(valores.cargo ?? ""),
            dataAdmissao: valores.dataAdmissao ?? null,
            salarioLiquido: Number(valores.salarioLiquido ?? 0),
            bancoCodigo: String(valores.bancoCodigo ?? ""),
            agencia: String(valores.agencia ?? ""),
            conta: String(valores.conta ?? ""),
            extras: linhasCarga[i].slice(COLUNAS_DECLARADAS_CARGA).map((c) => c.trim()),
        });
    }
    // Salario liquido e' base da margem e obrigatorio em TODA linha da carga
    // (vale para carga inicial e para o retorno). Ausente/invalido → pacote
    // recusado por inteiro, apontando os registros pra correcao e reenvio.
    if (errosSalario.length > 0) {
        out.linhasRejeitadas.push(...errosSalario);
        out.motivoRejeicao =
            `${errosSalario.length} registro(s) de carga sem salario liquido valido. ` +
                `O salario liquido e' a base da margem e e' obrigatorio em toda linha; ` +
                `o pacote inteiro foi recusado. Corrija os registros apontados e reenvie.`;
        return out;
    }
    if (errosDeclaracao.length > 0) {
        out.linhasRejeitadas.push(...errosDeclaracao);
        out.motivoRejeicao =
            `${errosDeclaracao.length} declaracao(oes) de desligamento invalida(s) na carga. ` +
                `Desligamento tira contrato da folha e notifica o banco, entao o pacote inteiro foi recusado. ` +
                `Corrija e reenvie.`;
        return out;
    }
    // ===== Uma competencia por pacote. Mistura significa arquivo montado errado,
    // e aplicar metade num mes e metade noutro corromperia as duas folhas.
    if (competencias.size > 1) {
        out.motivoRejeicao = `a carga mistura competencias (${[...competencias].sort().join(", ")}) — um pacote cobre UMA competencia`;
        return out;
    }
    out.competencia = [...competencias][0] ?? null;
    // ===== Consignados. Linha a linha: uma verba errada nao invalida o resto.
    for (let i = 0; i < linhasCons.length; i++) {
        const numero = i + 1;
        const { valores, erros } = parsearLinhaColunas(COLUNAS_CONSIGNADOS, linhasCons[i]);
        // Provento nao e' desconto. O ERP as vezes despeja a folha inteira aqui;
        // ignorar em silencio seria perder o sinal, entao conta.
        if (String(valores.tipo ?? "") === "P") {
            out.proventosIgnorados += 1;
            continue;
        }
        const total = Number(valores.parcelasTotal ?? 0);
        const atual = Number(valores.parcelaAtual ?? 0);
        if (total > 0 && atual > total) {
            erros.push({ campo: "parcelaAtual", coluna: 8, mensagem: `parcela ${atual} de um total de ${total}` });
        }
        if (erros.length > 0) {
            out.linhasRejeitadas.push({ arquivo: ARQUIVO_CONSIGNADOS, linha: numero, erros });
            continue;
        }
        out.consignados.push({
            linha: numero,
            matricula: String(valores.matricula ?? ""),
            codigoVerba: String(valores.codigoVerba ?? ""),
            descricao: String(valores.descricao ?? ""),
            tipo: String(valores.tipo ?? "D"),
            valor: Number(valores.valor ?? 0),
            parcelasRestantes: Number(valores.parcelasRestantes ?? 0),
            referencia: String(valores.referencia ?? ""),
            parcelaAtual: atual,
            parcelasTotal: total,
        });
    }
    out.rejeitado = false;
    out.motivoRejeicao = null;
    return out;
}
const cent = (v) => Math.round(v * 100);
/**
 * Casa uma linha de consignado com uma divida conhecida.
 *
 * A coluna de numero do contrato resolve isso sem ambiguidade, e e' o que
 * pedimos. Mas o pacote real que motivou este layout vem com ela VAZIA — e o
 * mesmo pacote tem duas dividas na mesma matricula e mesma verba
 * (specs/domain/referencia-consigfacil.md). Entao existe fallback por
 * atributos; e quando o fallback empata, a linha NAO e' adivinhada: casar
 * errado amortizaria a divida do contrato errado e o servidor pagaria um
 * contrato enquanto o outro segue em aberto.
 */
/**
 * Identidade de uma divida herdada, para a ADF deterministica.
 *
 * No ciclo mensal a coluna 7 (`referencia`) devolve a ADF que veio na remessa e
 * resolve tudo. Na PRIMEIRA carga nao existe ADF ainda, e no arquivo real do
 * ConsigFacil essa coluna vem vazia — sem outra chave, as tres dividas do mesmo
 * servidor colapsariam numa ADF so' e a ultima sobrescreveria as outras.
 *
 * O fallback usa os mesmos atributos que `casarConsignado` ja usa pra desempatar:
 * verba, mais o valor da parcela. A descricao entra porque e' o que distingue
 * duas consignatarias dentro da MESMA verba — caso comum em prefeitura que usa
 * uma rubrica unica pra "emprestimo consignado".
 *
 * Valor entra em CENTAVOS pra nao depender de como o float foi parseado.
 *
 * Duas linhas identicas em tudo produzem a mesma chave, e isso e' proposital:
 * nao ha informacao no arquivo que as distinga. Quem aplica a carga tem que
 * ACUSAR a colisao, nunca fundir em silencio.
 */
export function chaveHerdada(linha) {
    const ref = linha.referencia.trim();
    if (ref !== "")
        return ref;
    const centavos = Math.round(linha.valor * 100);
    return [
        normalizarAlfa(linha.codigoVerba),
        normalizarAlfa(linha.descricao),
        String(centavos),
    ].join("|");
}
/**
 * ORDINAL de cada linha dentro do arquivo: a n-esima ocorrencia da mesma
 * `matricula|chaveHerdada`.
 *
 * Duas linhas identicas em matricula, verba, descricao e valor sao DUAS dividas
 * (ADR-0010): o arquivo e' a verdade sobre o que a folha desconta, e se ele traz
 * duas linhas o servidor paga duas. Ate' 10/09/2026 a segunda colapsava na
 * primeira e era listada como "indistinguivel" para decisao humana (#98).
 *
 * O ordinal e' o que permite as duas coexistirem SEM quebrar a reimportacao: a
 * primeira ocorrencia mantem a chave antiga (contrato ja gravado continua
 * encontravel), e a segunda ganha `#2`. Reenviar o mesmo arquivo produz as
 * mesmas chaves; um arquivo com tres linhas iguais cria a terceira.
 *
 * Contado por MATRICULA, nao por arquivo inteiro: a mesma verba e valor em
 * servidores diferentes sao dividas de pessoas diferentes, e cada uma e' a
 * primeira ocorrencia da sua.
 */
export function ordinalDasLinhas(linhas) {
    const vistos = new Map();
    const ordinal = new Map();
    for (const l of linhas) {
        const k = `${l.matricula}|${chaveHerdada(l)}`;
        const n = (vistos.get(k) ?? 0) + 1;
        vistos.set(k, n);
        ordinal.set(l.linha, n);
    }
    return ordinal;
}
/** `matricula|chave` para a primeira ocorrencia; `matricula|chave#n` da segunda em diante. */
export function chaveLegadoDaLinha(linha, ordinal) {
    const base = `${linha.matricula}|${chaveHerdada(linha)}`;
    return ordinal > 1 ? `${base}#${ordinal}` : base;
}
/** O ordinal gravado numa `chaveLegado`. Sem sufixo = primeira ocorrencia. */
export function ordinalDaChaveLegado(chaveLegado) {
    const m = /#(\d+)$/.exec(chaveLegado ?? "");
    return m ? Number(m[1]) : 1;
}
export function casarConsignado(linha, dividas, opts = {}) {
    // CONSUMO (ADR-0010): divida que outra linha deste arquivo ja' casou esta'
    // fora. E' o que faz duas linhas identicas caerem em dois contratos, uma em
    // cada, na ordem do arquivo — e a terceira linha, sem contrato livre, virar
    // `desconhecido` e ganhar contrato proprio.
    const jaCasadas = opts.jaCasadas;
    const daMatricula = dividas
        .filter((d) => d.matricula === linha.matricula)
        .filter((d) => !jaCasadas || !jaCasadas.has(d.adf))
        // Ordem estavel: ordinal, depois ADF. E' ela que diz quem e' "a primeira".
        .slice()
        .sort((a, b) => (a.ordinal ?? 1) - (b.ordinal ?? 1) || a.adf.localeCompare(b.adf));
    const consumir = (candidatas) => jaCasadas && candidatas.every((d) => d.ordinal !== undefined)
        ? { motivo: "consumo", adf: candidatas[0].adf, candidatas: candidatas.map((d) => d.adf) }
        : { motivo: "ambiguo", adf: null, candidatas: candidatas.map((d) => d.adf) };
    // 1) Identificador explicito: ADF ou referencia de origem.
    const ref = linha.referencia.trim();
    if (ref !== "") {
        const porId = daMatricula.find((d) => d.adf === ref || (d.referencia ?? "") === ref);
        if (porId)
            return { motivo: "identificador", adf: porId.adf, candidatas: [] };
    }
    // 2) Fallback por atributos. Verba estreita; VALOR desempata; parcela desempata
    //    o que o valor nao resolveu.
    const mesmaVerba = daMatricula.filter((d) => linha.codigoVerba === "" || d.codigoVerba === "" || d.codigoVerba === linha.codigoVerba);
    // A PARCELA E' DESEMPATE, NAO PRE-CONDICAO.
    //
    // Antes as duas condicoes moravam no MESMO filtro (`valor bate E parcela
    // concorda`). Quando o valor identificava uma unica divida e a parcela nao
    // concordava, o filtro esvaziava — e o fallback seguinte, que so' olha a verba,
    // achava duas candidatas e declarava AMBIGUO. O valor tinha resolvido e a
    // resposta era jogada fora.
    //
    // Onde mordia: contrato LEGADO. A folha e' a autoridade sobre o cronograma dele
    // (`cronogramaAutoritativo: "folha"`), entao exigir que a NOSSA contagem de
    // parcela concorde com a DELA e' ao contrario. Duas dividas do mesmo servidor
    // na mesma verba, com valores bem distintos (180,00 e 240,00), caiam em
    // pendencia de casamento TODO MES — no unico caso que a desambiguacao por valor
    // existe pra resolver. Achado pelo E2E do pacote modelo, 27/08/2026.
    const porValor = mesmaVerba.filter((d) => cent(d.valorParcela) === cent(linha.valor));
    if (porValor.length === 1) {
        return { motivo: "atributos", adf: porValor[0].adf, candidatas: [] };
    }
    if (porValor.length > 1) {
        // O valor empatou — agora sim a parcela decide. E' pra isto que ela serve:
        // duas dividas de MESMO valor na mesma verba.
        const porParcela = porValor.filter((d) => linha.parcelaAtual !== 0 && d.parcelaInstruida !== 0 && d.parcelaInstruida === linha.parcelaAtual);
        if (porParcela.length === 1) {
            return { motivo: "atributos", adf: porParcela[0].adf, candidatas: [] };
        }
        // Indistinguiveis no arquivo. Com `jaCasadas`, a linha consome a primeira
        // livre na ordem do arquivo (as dividas sao identicas por definicao, entao a
        // escolha e' neutra); sem ela, nao adivinha.
        return consumir(porParcela.length > 1 ? porParcela : porValor);
    }
    // Sem valor batendo: se ha exatamente uma divida na verba, ainda casa — o
    // valor divergir e' justamente o caso "parcial", que precisa ser registrado.
    if (mesmaVerba.length === 1) {
        return { motivo: "atributos", adf: mesmaVerba[0].adf, candidatas: [] };
    }
    if (mesmaVerba.length > 1) {
        // Valores DIFERENTES entre si e nenhum igual ao da linha: aqui consumir
        // seria adivinhar entre dividas distinguiveis, e o ADR-0010 so' autoriza o
        // consumo entre identicas. Continua ambiguo.
        return { motivo: "ambiguo", adf: null, candidatas: mesmaVerba.map((d) => d.adf) };
    }
    return { motivo: "desconhecido", adf: null, candidatas: [] };
}
/**
 * Deduz o resultado do desconto a partir do valor que a folha descontou.
 *
 * O arquivo de consignados nao tem coluna de "resultado" — ele so lista o que
 * FOI descontado. Linha ausente significa que nao houve desconto, e isso e'
 * decidido por quem chama (compara o instruido com o que voltou).
 */
export function resultadoPorValor(valorInstruido, valorDescontado) {
    const inst = cent(valorInstruido);
    const pago = cent(valorDescontado);
    if (pago <= 0)
        return "falha";
    if (pago >= inst)
        return "aplicada";
    return "parcial";
}
/** Reexporta o mapa de codigos pra quem monta relatorio a partir do resultado. */
export { CODIGO_RESULTADO };
//# sourceMappingURL=retorno.js.map